/**
 * Hook do zarządzania logiką parametrów dla WiseChart
 * Obsługuje tylko parametry typu range i date_range
 */

import { Dispatch, SetStateAction, useMemo } from 'react';
import { mapValues, keyBy } from 'lodash';

import { WiseQueryParams, IActiveParameter, IWiseChartParameter } from 'components/wise/types';

interface IUseWiseChartParametersParams {
  parameters: IWiseChartParameter[];
  queryParams: WiseQueryParams;
  setQueryParams: Dispatch<SetStateAction<WiseQueryParams>>;
}

interface IUseWiseChartParametersReturn {
  parameters: IWiseChartParameter[];
  activeParameters: IActiveParameter[];
  handleClearParameters: () => void;
  removeParameter: (fieldSymbol: string) => void;
  updateParameter: (fieldSymbol: string, value: any) => void;
}

/**
 * Hook do zarządzania logiką parametrów dla WiseChart
 */
export const useWiseChartParameters = ({
  parameters,
  queryParams,
  setQueryParams
}: IUseWiseChartParametersParams): IUseWiseChartParametersReturn => {
  // Aktualizacja parametru
  const updateParameter = (fieldSymbol: string, value: any): void => {
    setQueryParams((prevState) => {
      return {
        ...prevState,
        [fieldSymbol]: value
      };
    });
  };

  // Czyszczenie wszystkich parametrów
  const handleClearParameters = (): void => {
    const defaultParams: WiseQueryParams = {
      ...(parameters
        ? mapValues(keyBy(parameters, 'field_symbol'), 'default_value')
        : {})
    };

    setQueryParams(defaultParams);
  };

  const activeParameters: IActiveParameter[] = useMemo(
    () =>
      parameters
        .filter((param) => {
          const value = queryParams[param.field_symbol];
          return value !== undefined && value !== null && value !== '';
        })
        .map((param) => ({
          fieldSymbol: param.field_symbol,
          label: param.label,
          value: queryParams[param.field_symbol],
          displayValue: String(queryParams[param.field_symbol])
        })),
    [parameters, queryParams]
  );

  // Usuwanie konkretnego parametru
  const removeParameter = (fieldSymbol: string): void => {
    updateParameter(fieldSymbol, '');
  };

  return {
    parameters,
    activeParameters,
    handleClearParameters,
    removeParameter,
    updateParameter
  };
};
